/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from '@/integrations/supabase/client';

/**
 * Singleton pour gérer le cache des buckets storage
 * Évite les requêtes dupliquées au démarrage de l'application
 */
class StorageManager {
  private static instance: StorageManager;
  private bucketsCache: any[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private pendingRequest: Promise<any> | null = null;

  private constructor() {}

  static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  /**
   * Récupère la liste des buckets avec mise en cache
   * Évite les appels dupliqués grâce au mécanisme de coalescing
   */
  async getBuckets() {
    const now = Date.now();
    
    // Retourner le cache si valide
    if (this.bucketsCache && (now - this.cacheTimestamp) < this.CACHE_TTL) {
      return { data: this.bucketsCache, error: null };
    }

    // Si une requête est déjà en cours, attendre son résultat
    if (this.pendingRequest) {
      return this.pendingRequest;
    }

    // Créer une nouvelle requête
    this.pendingRequest = supabase.storage.listBuckets()
      .then(({ data, error }) => {
        if (!error && data) {
          this.bucketsCache = data;
          this.cacheTimestamp = now;
        }
        this.pendingRequest = null;
        return { data, error };
      })
      .catch((error) => {
        this.pendingRequest = null;
        return { data: null, error };
      });

    return this.pendingRequest;
  }

  /**
   * Vérifie si un bucket existe
   */
  async bucketExists(bucketId: string): Promise<boolean> {
    const { data } = await this.getBuckets();
    return data?.some(bucket => bucket.id === bucketId) || false;
  }

  /**
   * Invalide le cache (à appeler après création/suppression de bucket)
   */
  clearCache() {
    this.bucketsCache = null;
    this.cacheTimestamp = 0;
    this.pendingRequest = null;
  }
}

export const storageManager = StorageManager.getInstance();
